import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TurniExtraInterface,
  TurniExtraItemInterface,
} from '../interfaces/extra-turni-point-of-sale';
import {
  checkAllExtraHours,
  checkExtraHoursValue,
} from '../components/PopUp/utils/check-extra-hours';
import { RootState } from '../../../store';
import { Dayjs } from 'dayjs';

/**
 * Tipo per i tipi di turni
 */
type TurnoType = 'calendar' | 'shift';

/**
 * Tipo per i numeri di intervallo
 */
type RangeNumber = 1 | 2;

/**
 * Interfaccia per gli errori di un turno
 */
interface TurnoErrors {
  range1: boolean;
  range2: boolean;
}

/**
 * Interfaccia per gli errori di tutti i turni per una chiave
 */
interface ExtraHoursErrorItem {
  shift: TurnoErrors;
  calendar: TurnoErrors;
}

/**
 * Interfaccia per lo stato degli orari extra
 */
export interface ExtraHoursInterface {
  extra: TurniExtraInterface;
  extraErrors: Record<string, ExtraHoursErrorItem>;
  openAddPeriodPopUp: boolean;
}

/**
 * Stato iniziale degli orari extra
 */
const initialState: ExtraHoursInterface = {
  extra: {},
  extraErrors: {},
  openAddPeriodPopUp: false,
};

/**
 * Slice Redux per gli orari extra dei punti vendita
 */
const PointOfSaleExtraHoursSlice = createSlice({
  name: 'PointOfSaleExtraHours',
  initialState,
  reducers: {
    /**
     * Imposta gli orari extra e aggiorna gli errori associati
     */
    setExtraHours(
      state: ExtraHoursInterface,
      action: PayloadAction<{ value: TurniExtraInterface; keyChanged?: string }>
    ) {
      const { value, keyChanged } = action.payload;
      state.extra = value;

      // Se abbiamo una chiave specifica, aggiorniamo solo gli errori per quella chiave
      if (keyChanged) {
        updateErrorsForKey(state, keyChanged, value[keyChanged]);
      } else {
        // Altrimenti, aggiorniamo tutti gli errori
        state.extraErrors = checkAllExtraHours(value);
      }
    },

    /**
     * Imposta il valore di un orario extra e aggiorna gli errori associati
     */
    setExtraHourValue(
      state: ExtraHoursInterface,
      action: PayloadAction<{
        value: {
          date: Dayjs;
          shift: TurniExtraItemInterface | null;
          calendar: TurniExtraItemInterface | null;
        };
        keyChanged: string;
        turniType?: TurnoType;
      }>
    ) {
      const { value, keyChanged } = action.payload;

      // Aggiorna il valore dell'orario
      state.extra = {
        ...state.extra,
        [keyChanged]: {
          ...state.extra[keyChanged],
          ...value,
        },
      };

      // Aggiorna gli errori per la chiave modificata
      updateErrorsForKey(state, keyChanged, value);
    },

    /**
     * Elimina un orario extra e rimuove i relativi errori
     */
    setDeleteExtraHour(
      state: ExtraHoursInterface,
      action: PayloadAction<{
        key: string;
        idShift: string | undefined;
        idCalendar: string | undefined;
      }>
    ) {
      const { key } = action.payload;

      // Crea un nuovo oggetto senza l'elemento eliminato
      const { [key]: _, ...remainingExtra } = state.extra;
      state.extra = remainingExtra;

      // Rimuovi anche gli errori associati
      if (key in state.extraErrors) {
        const { [key]: __, ...remainingErrors } = state.extraErrors;
        state.extraErrors = remainingErrors;
      }
    },

    /**
     * Imposta lo stato del popup per aggiungere un periodo
     */
    setOpenAddPeriodPopUp(
      state: ExtraHoursInterface,
      action: PayloadAction<boolean>
    ) {
      state.openAddPeriodPopUp = action.payload;
    },

    /**
     * Reimposta lo stato agli valori iniziali
     */
    resetExtraHours() {
      return initialState;
    },
  },
});

/**
 * Funzione helper per aggiornare gli errori per una chiave specifica
 */
function updateErrorsForKey(
  state: ExtraHoursInterface,
  key: string,
  value: any
) {
  // Inizializza la struttura degli errori se non esiste
  if (!state.extraErrors[key]) {
    state.extraErrors[key] = {
      shift: { range1: false, range2: false },
      calendar: { range1: false, range2: false },
    };
  }

  // Aggiorna gli errori per il turno shift
  state.extraErrors[key].shift.range1 = checkExtraHoursValue(1, 'shift', value);
  state.extraErrors[key].shift.range2 = checkExtraHoursValue(2, 'shift', value);

  // Aggiorna gli errori per il turno calendar
  state.extraErrors[key].calendar.range1 = checkExtraHoursValue(
    1,
    'calendar',
    value
  );
  state.extraErrors[key].calendar.range2 = checkExtraHoursValue(
    2,
    'calendar',
    value
  );
}

export const {
  setOpenAddPeriodPopUp,
  setExtraHours,
  setExtraHourValue,
  setDeleteExtraHour,
  resetExtraHours,
} = PointOfSaleExtraHoursSlice.actions;

/**
 * Selettore per ottenere lo stato di errore per un intervallo specifico
 */
export const extraHourValueError = (
  state: RootState,
  turniType: TurnoType,
  key: string,
  rangeNumber: RangeNumber
): boolean => {
  const errors = state.bluBookingPointOfSaleExtraHours.extraErrors;
  return key in errors ? errors[key][turniType][`range${rangeNumber}`] : false;
};

/**
 * Selettore per determinare se ci sono errori nei dati degli orari extra
 */
export const hasErrorExtraHourValue = (state: RootState): boolean => {
  const errors = state.bluBookingPointOfSaleExtraHours.extraErrors;
  return Object.values(errors).some(
    (error) =>
      error.shift.range1 ||
      error.shift.range2 ||
      error.calendar.range1 ||
      error.calendar.range2
  );
};

export default PointOfSaleExtraHoursSlice.reducer;
